import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import {
  Wind, MapPin, Phone, User, Clock, CreditCard,
  CheckCircle, ExternalLink, Map, Receipt
} from 'lucide-react';
import ReceiptEditor from './ReceiptEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RentalDetailsModal = ({ rental, open, onOpenChange, token, language, onUpdate }) => {
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState(rental?.status || 'received');
  const [showReceipt, setShowReceipt] = useState(false);

  if (!rental) return null;

  const statusOptions = [
    { value: 'received', label: language === 'pt' ? 'Recebido' : 'Received', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'preparing', label: language === 'pt' ? 'Preparando' : 'Preparing', color: 'bg-blue-100 text-blue-700' },
    { value: 'ready', label: language === 'pt' ? 'Pronto' : 'Ready', color: 'bg-purple-100 text-purple-700' },
    { value: 'out_for_delivery', label: language === 'pt' ? 'Em Entrega' : 'Out for Delivery', color: 'bg-orange-100 text-orange-700' },
    { value: 'delivered', label: language === 'pt' ? 'Entregue' : 'Delivered', color: 'bg-green-100 text-green-700' },
    { value: 'cancelled', label: language === 'pt' ? 'Cancelado' : 'Cancelled', color: 'bg-red-100 text-red-700' },
  ];
  const s = statusOptions.find(o => o.value === rental.status) || statusOptions[0];

  const handleStatusChange = async () => {
    if (newStatus === rental.status) return;
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${rental.rental_id}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(language === 'pt' ? 'Status atualizado!' : 'Status updated!');
        onUpdate?.();
        onOpenChange?.(false);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || (language === 'pt' ? 'Erro ao atualizar' : 'Error updating'));
      }
    } catch (e) {
      toast.error(language === 'pt' ? 'Erro de rede' : 'Network error');
    } finally {
      setUpdating(false);
    }
  };

  const openMapLocation = () => {
    if (rental.gps_coordinates) {
      const [lat, lng] = rental.gps_coordinates.split(',').map(x => x.trim());
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
    } else if (rental.delivery_location) {
      window.open(`https://www.google.com/maps/search/${encodeURIComponent(rental.delivery_location)}`, '_blank');
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rental-details-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Wind className="text-purple-600" size={20} />
              {language === 'pt' ? 'Aluguer' : 'Rental'} #{rental.rental_id?.slice(-8)}
            </span>
            <Badge className={s.color}>{s.label}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Items */}
          <div className="bg-[#F4F1EA] rounded-xl p-4">
            <h4 className="font-semibold text-[#004E64] mb-3 text-sm">
              {language === 'pt' ? 'Itens do Aluguer' : 'Rental Items'}
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#004E64]">1x Shisha {rental.shisha_name} — {rental.flavor_name} ({rental.duration_hours}h)</span>
                <span className="font-semibold text-[#FF6B35]">€{Number(rental.rental_price || 0).toFixed(2)}</span>
              </div>
              {rental.extra_charcoal && <div className="text-[#004E64]">1x {language === 'pt' ? 'Carvão extra' : 'Extra charcoal'}</div>}
              {rental.extra_hose && <div className="text-[#004E64]">1x {language === 'pt' ? 'Mangueira extra' : 'Extra hose'}</div>}
              {(rental.selected_extras || []).map((ex, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-[#004E64]">{ex.quantity || 1}x {ex.name}</span>
                  <span className="font-semibold text-[#FF6B35]">€{Number(ex.line_total ?? ((ex.price || 0) * (ex.quantity || 1))).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <Separator className="my-3" />
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-[#9FA2B4]">
                <span>{language === 'pt' ? 'Preço Aluguer' : 'Rental Price'}</span>
                <span>€{Number(rental.rental_price || 0).toFixed(2)}</span>
              </div>
              {Number(rental.extras_price || 0) > 0 && (
                <div className="flex justify-between text-[#9FA2B4]">
                  <span>Extras</span>
                  <span>€{Number(rental.extras_price).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-[#9FA2B4]">
                <span>{language === 'pt' ? 'Taxa Entrega' : 'Delivery Fee'}</span>
                <span>€{Number(rental.delivery_fee || 0).toFixed(2)}</span>
              </div>
              {Number(rental.deposit || 0) > 0 && (
                <div className="flex justify-between text-[#9FA2B4]">
                  <span>{language === 'pt' ? 'Depósito' : 'Deposit'}</span>
                  <span>€{Number(rental.deposit).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-[#004E64]">
                <span>Total</span>
                <span className="text-[#FF6B35]">€{Number(rental.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Customer */}
          <div className="space-y-2">
            <h4 className="font-semibold text-[#004E64] text-sm flex items-center gap-2">
              <User size={16} className="text-[#FF6B35]" />
              {language === 'pt' ? 'Cliente' : 'Customer'}
            </h4>
            <div className="pl-6 space-y-1 text-sm">
              <p className="text-[#004E64]">{rental.customer_name}</p>
              <p className="text-[#9FA2B4] flex items-center gap-1"><Phone size={12} /> {rental.phone}</p>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <h4 className="font-semibold text-[#004E64] text-sm flex items-center gap-2">
              <MapPin size={16} className="text-[#FF6B35]" />
              {language === 'pt' ? 'Local de Entrega' : 'Delivery Location'}
            </h4>
            <div className="pl-6 space-y-1 text-sm">
              <p className="text-[#004E64]">{rental.delivery_location}</p>
              {rental.delivery_time && <p className="text-[#9FA2B4]">{rental.delivery_time}</p>}
              {rental.gps_coordinates && <p className="text-[#2EC4B6] text-xs">📍 GPS: {rental.gps_coordinates}</p>}
              <Button variant="outline" size="sm" onClick={openMapLocation} className="mt-2 rounded-full text-xs">
                <Map size={12} className="mr-1" />
                {language === 'pt' ? 'Ver no Mapa' : 'View on Map'}
                <ExternalLink size={10} className="ml-1" />
              </Button>
            </div>
          </div>

          {/* Payment */}
          <div className="space-y-2">
            <h4 className="font-semibold text-[#004E64] text-sm flex items-center gap-2">
              <CreditCard size={16} className="text-[#FF6B35]" />
              {language === 'pt' ? 'Pagamento' : 'Payment'}
            </h4>
            <div className="pl-6 space-y-2 text-sm">
              <p className="text-[#004E64]">{rental.payment_method === 'card' ? 'Cartão' : rental.payment_method}</p>
              <Badge className={rental.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                {rental.payment_status === 'paid' ? '✓ Pago' : rental.payment_status === 'refunded' ? '↩ Reembolsado' : '⏳ Pendente'}
              </Badge>
            </div>
          </div>

          {rental.notes && (
            <div className="bg-[#F4F1EA] rounded-xl p-3">
              <p className="text-sm text-[#9FA2B4]"><strong>{language === 'pt' ? 'Notas:' : 'Notes:'}</strong> {rental.notes}</p>
            </div>
          )}

          <div className="text-xs text-[#9FA2B4] flex items-center gap-1">
            <Clock size={12} />
            {language === 'pt' ? 'Criado em:' : 'Created:'} {rental.created_at ? new Date(rental.created_at).toLocaleString('pt-PT') : ''}
          </div>

          <Button
            variant="outline"
            onClick={() => setShowReceipt(true)}
            className="w-full rounded-full border-[#FF6B35]/40 text-[#FF6B35]"
            data-testid="rental-receipt-btn"
          >
            <Receipt size={16} className="mr-2" />
            {language === 'pt' ? 'Recibo (ver / editar / enviar)' : 'Receipt (view / edit / send)'}
          </Button>

          <Separator />

          {/* Change Status */}
          <div className="space-y-3">
            <h4 className="font-semibold text-[#004E64] text-sm">{language === 'pt' ? 'Alterar Status' : 'Change Status'}</h4>
            <div className="flex gap-2">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="flex-1" data-testid="rental-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleStatusChange}
                disabled={updating || newStatus === rental.status}
                className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                data-testid="rental-status-update-btn"
              >
                <CheckCircle size={16} className="mr-1" />
                {language === 'pt' ? 'Atualizar' : 'Update'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <ReceiptEditor entityId={rental.rental_id} open={showReceipt} onOpenChange={setShowReceipt} token={token} language={language} />
    </>
  );
};

export default RentalDetailsModal;
